import { useTheme } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useState, useEffect, useLayoutEffect, useRef } from "react";

const VIEWPORT_MARGIN = 8;
const OPPOSITE_PLACEMENT = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * @typedef {Object} Step
 * @property {string} title Título do Passo
 * @property {string} content Conteúdo renderizado no passo. Pode ser um texto ou um elemento
 * @property {function} target Uma função no formato que retorne o elemento a ser destacado. Caso não seja informado o passo é exibido no centro da tela
 * @property {'top' | 'right' | 'bottom' | 'left'} placement Posicionamento do card relativo ao elemento em target
 * @property {boolean} enableInteraction Permite clicar e interagir com o elemento destacado
 * @property {string} primaryButtonText Texto do botão que passa para o próximo passo
 * @property {string} overlay Cor no overlay exibido no fundo
 * @property {number} overlayOpacity Número entre 0 e 1 para definir a opacidade do overlay
 * @property {boolean} triggerNext Valor que ao mudar para true, passa para o próxmimo passo. Útil quando é necessário passar para o próximo passo baseado numa ação do usuário, como clicar no elmento destacado
 * @property {boolean} hideBack Esconde o botão de voltar
 * @property {boolean} showButtons Caso falso, esconde todos os botões de navegação. A navegação para o próximo passo fica dependente do triggerNext
 * @property {boolean} disableNext Desabilita o botão de continuar
 * @property {function} onNextAction Função para ser executada ao clicar no botão de continuar. A função passa como parâmetro um callback que ao executar passa para o próximo passo.
 */

/**
 * Componente de Tour
 * @property {boolean} open 
 * @property {Step[]} steps Passos do Tour
 * @property {function} onClose Função executada ao fechar o Tour 
 * @property {function} onFinish Função executada ao finalizar o Tour
 * @property {number} containerWidth Largura do card 
 * @property {number} highlightPadding Padding do elemento destacado
 * @property {number} containerOffset Distância do card ao elemento destacado 
 */
export default function Tour({
    open,
    steps,
    onClose = () => { },
    onFinish = () => { },
    containerWidth = 520,
    highlightPadding: highlightPaddingProp = 4,
    containerOffset = 24,
    toggleScroll = true
}) {

    const defaultPosition = {
        left: window.innerWidth / 2,
        top: window.innerHeight / 2,
        width: 0,
        height: 0,
    };

    const theme = useTheme();

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [scrollPositioned, setScrollPositioned] = useState(false);

    const currentStep = steps[currentStepIndex];
    const componentRef = currentStep.target && currentStep.target() ? currentStep.target() : null;
    const componentPos = componentRef ? componentRef.getBoundingClientRect() : defaultPosition;

    const containerMargin = componentRef ? containerOffset : 0;
    const highlightPadding = componentRef ? highlightPaddingProp : 0;

    const containerRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const [containerHeight, setContainerHeight] = useState(0);
    const [, setLayoutTick] = useState(0);
    const forceLayout = () => setLayoutTick(tick => tick + 1);

    // Rola o alvo para o centro da tela, inclusive dentro de containers com scroll próprio
    const scrollIntoView = () => {
        if (!componentRef || typeof componentRef.scrollIntoView !== 'function') return;
        componentRef.scrollIntoView({ block: 'center', inline: 'nearest' });
    }

    // Trava o scroll da página enquanto o tour está aberto e devolve o valor anterior ao fechar ou desmontar
    useEffect(() => {
        if (!open || !toggleScroll) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open, toggleScroll]);

    // Esc sempre fecha o tour (mesmo efeito do botão fechar)
    useEffect(() => {
        if (!open) return;
        const handleKeyDown = event => {
            if (event.key === 'Escape' || event.key === 'Esc') onCloseRef.current();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    // Remede posições quando a janela muda de tamanho/zoom ou algo rola
    useEffect(() => {
        if (!open) return;
        let frame = null;
        const handleLayoutChange = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = null;
                forceLayout();
            });
        };
        window.addEventListener('resize', handleLayoutChange);
        window.addEventListener('scroll', handleLayoutChange, true);
        return () => {
            window.removeEventListener('resize', handleLayoutChange);
            window.removeEventListener('scroll', handleLayoutChange, true);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [open]);

    useEffect(() => {

        setScrollPositioned(false);

        if (!open) {
            setCurrentStepIndex(0);
            return;
        };

        scrollIntoView();

        setScrollPositioned(true);
        forceLayout();

    }, [open, componentRef]);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const height = containerRef.current.offsetHeight;
        if (height !== containerHeight) setContainerHeight(height);
    });

    const changeStep = (amount) => {
        const newIndex = currentStepIndex + amount;

        if (newIndex >= steps.length) {
            onFinish();
            return;
        }

        setCurrentStepIndex(newIndex);
        setScrollPositioned(false);
    }

    const handleGoToNext = () => {
        if (!currentStep.onNextAction) {
            changeStep(1);
            return;
        }

        currentStep.onNextAction(() => changeStep(1));
    }

    useEffect(() => {
        if (!currentStep.triggerNext) return;
        changeStep(1);
    }, [currentStep.triggerNext]);

    const renderOverlay = () => {

        const overlayColor = currentStep.overlay || '#000';
        const overlayOpacity = currentStep.overlayOpacity = 0.5;

        const commonStyle = {
            position: 'fixed',
            backgroundColor: overlayColor,
            zIndex: 9999,
            top: 0,
            left: 0,
            transition: 'all .3s ease',
        };

        const sides = [
            {
                key: 'top',
                left: componentPos.left - highlightPadding,
                width: componentPos.width + highlightPadding * 2,
                height: componentPos.top - highlightPadding,
            },
            {
                key: 'right',
                left: componentPos.left + componentPos.width + highlightPadding,
                width: `calc(100% - ${componentPos.left + componentPos.width + highlightPadding}px)`,
                height: '100%',
            },
            {
                key: 'bottom',
                top: componentPos.top + componentPos.height + highlightPadding,
                left: componentPos.left - highlightPadding,
                width: componentPos.width + highlightPadding * 2,
                height: `calc(100% - ${componentPos.top + componentPos.height + highlightPadding}px)`,
            },
            {
                key: 'left',
                width: Math.max(componentPos.left - highlightPadding, 0),
                height: '100%',
            },
        ];

        return (
            <div
                style={{ opacity: overlayOpacity, position: 'fixed', zIndex: 9999 }}
            >
                {sides.map(side => (
                    <div key={side.key} style={{ ...commonStyle, ...side }} />
                ))}
            </div>
        )
    }

    const getContainerWidth = () => Math.min(containerWidth, window.innerWidth - VIEWPORT_MARGIN * 2);

    // Escolhe um lado que caiba na tela (preferindo o placement do passo) e mantém a caixa sempre dentro da viewport
    const getContainerPosition = (refDimensions) => {
        const width = getContainerWidth();
        const height = containerHeight;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const clampToViewport = ({ left, top }) => ({
            left: clamp(left, VIEWPORT_MARGIN, viewportWidth - width - VIEWPORT_MARGIN),
            top: clamp(top, VIEWPORT_MARGIN, viewportHeight - height - VIEWPORT_MARGIN),
        });

        if (!componentRef) {
            return {
                placement: null,
                width,
                ...clampToViewport({ left: (viewportWidth - width) / 2, top: (viewportHeight - height) / 2 }),
            };
        }

        const centeredLeft = refDimensions.left + refDimensions.width / 2 - width / 2;
        const centeredTop = refDimensions.top + refDimensions.height / 2 - height / 2;

        const candidates = {
            top: { left: centeredLeft, top: refDimensions.top - containerMargin - height },
            bottom: { left: centeredLeft, top: refDimensions.top + refDimensions.height + containerMargin },
            left: { left: refDimensions.left - containerMargin - width, top: centeredTop },
            right: { left: refDimensions.left + refDimensions.width + containerMargin, top: centeredTop },
        };

        const fits = {
            top: candidates.top.top >= VIEWPORT_MARGIN,
            bottom: candidates.bottom.top + height <= viewportHeight - VIEWPORT_MARGIN,
            left: candidates.left.left >= VIEWPORT_MARGIN,
            right: candidates.right.left + width <= viewportWidth - VIEWPORT_MARGIN,
        };

        const preferred = candidates[currentStep.placement] ? currentStep.placement : 'top';
        const order = [preferred, OPPOSITE_PLACEMENT[preferred], 'bottom', 'top', 'right', 'left'];
        const placement = order.find(side => fits[side]) || null;

        return {
            placement,
            width,
            ...clampToViewport(candidates[placement || preferred]),
        };
    }

    const getArrowPosition = (refDimensions, container) => {
        const { placement } = container;
        if (!componentRef || !placement) return null;

        const arrowInset = 16;
        const clampX = left => clamp(left, container.left + arrowInset, container.left + container.width - arrowInset);
        const clampY = top => clamp(top, container.top + arrowInset, container.top + containerHeight - arrowInset);

        const placements = {
            right: () => ({
                top: clampY(refDimensions.top + refDimensions.height / 2),
                left: container.left,
            }),
            bottom: () => ({
                top: container.top,
                left: clampX(refDimensions.left + refDimensions.width / 2),
            }),
            left: () => ({
                top: clampY(refDimensions.top + refDimensions.height / 2),
                left: container.left + container.width,
            }),
            top: () => ({
                top: container.top + containerHeight,
                left: clampX(refDimensions.left + refDimensions.width / 2),
            }),
        }

        return placements[placement]();
    }

    const getRefDimensions = () => {
        const refDimensions = {
            width: componentPos.width + highlightPadding * 2,
            height: componentPos.height + highlightPadding * 2,
            top: componentPos.top - highlightPadding,
            left: componentPos.left - highlightPadding,
        };

        return refDimensions;
    }

    const getPrimaryButtonText = () => {
        if (currentStep.primaryButtonText) return currentStep.primaryButtonText;
        return currentStepIndex < steps.length - 1 ? 'Continuar' : 'Finalizar'
    }

    const renderContentContainer = () => {

        const refDimensions = getRefDimensions();
        const container = getContainerPosition(refDimensions);
        const arrowPosition = getArrowPosition(refDimensions, container);

        const enableInteraction = !!currentStep.enableInteraction;

        return (
            <div
                className="fixed w-full h-full left-0 top-0 z-[10000]"
                style={{ pointerEvents: enableInteraction ? 'none' : 'all' }}
            >
                <div
                    className="w-3 h-3 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-white rounded"
                    style={{
                        position: 'fixed',
                        zIndex: 10000,
                        display: arrowPosition ? 'block' : 'none',
                        ...(arrowPosition || {}),
                    }}
                />
                <div
                    ref={containerRef}
                    className="pointer-events-auto rounded-lg"
                    role="dialog"
                    aria-modal="true"
                    aria-label={currentStep.title}
                    style={{
                        position: 'fixed',
                        zIndex: 10000,
                        left: container.left,
                        top: container.top,
                        width: container.width,
                        maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
                        overflowY: 'auto',
                    }}
                >
                    <div className="p-8 rounded-lg text-base bg-white">
                        <div className="flex justify-between gap-3">
                            <div className="font-semibold">{currentStep.title}</div>
                            <div>
                                <button aria-label="Fechar tour" title="Fechar (Esc)" onClick={onClose}>
                                    <Close className="text-[#0009] hover:text-[#000] !transition-[color_.3s_ease,_box-shadow_.3s_ease] hover:shadow-[inset_0px_0_0px_12px_#0001,_0_0_0_1px_#0001]" />
                                </button>
                            </div>
                        </div>
                        <div className="mt-3">{currentStep.content}</div>
                        <div className="flex justify-between mt-4">
                            <div className="flex gap-2 items-center min-h-7 h-auto">
                                {steps.map((step, index) => (
                                    <div
                                        key={index}
                                        className="h-2 w-2 rounded-full shrink-0"
                                        style={{
                                            background: currentStepIndex === index ? theme.palette.primary.main : '#0003',
                                        }}
                                    />
                                ))}
                            </div>
                            <div className="flex gap-2">
                                {currentStep.showButtons !== false && (
                                    <>
                                        {currentStepIndex > 0 && !currentStep.hideBack && (
                                            <button
                                                className="px-2 py-1 text-sm rounded" style={{ border: 'solid 1px #dedede' }}
                                                onClick={() => changeStep(-1)}
                                            >
                                                Voltar
                                            </button>
                                        )}
                                        <button
                                            className="px-2 py-1 text-sm rounded text-white"
                                            onClick={handleGoToNext}
                                            style={{
                                                background: theme.palette.primary.main,
                                                pointerEvents: currentStep.disableNext ? 'none' : undefined,
                                                filter: currentStep.disableNext ? 'saturate(0)' : undefined,
                                            }}
                                        >
                                            {getPrimaryButtonText()}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (!open) return <></>;

    return (
        <>
            {renderOverlay()}
            {scrollPositioned && renderContentContainer()}
        </>
    );
}